import React, { useState } from 'react';
import { Logo1, Logo2, Logo3, Logo4, Logo5, Logo6, Logo7, Logo8, Logo9, Logo10 } from '../components/LogoOptions';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle2 } from 'lucide-react';

const LogoGallery = () => {
  const [selected, setSelected] = useState(null);

  const logos = [
    { id: 1, name: "Iris Eye Hexagon", description: "Eye monitoring with AI neural network", component: Logo1, concept: "Vision + Intelligence" },
    { id: 2, name: "Network Mesh", description: "Interconnected security network", component: Logo2, concept: "Distributed Security" },
    { id: 3, name: "Lock Circuit", description: "Digital lock with circuit connections", component: Logo3, concept: "Secure Technology" },
    { id: 4, name: "DNA Helix Data", description: "Research data structure", component: Logo4, concept: "Scientific Innovation" },
    { id: 5, name: "Radar Pulse", description: "Active threat monitoring radar", component: Logo5, concept: "Real-time Detection" },
    { id: 6, name: "Data Shield", description: "Protected data verification", component: Logo6, concept: "Data Protection" },
    { id: 7, name: "Neural Brain", description: "AI brain with neural pathways", component: Logo7, concept: "AI Intelligence" },
    { id: 8, name: "Crystal Cube", description: "3D geometric security structure", component: Logo8, concept: "Multi-layered Security" },
    { id: 9, name: "Infinity Network", description: "Continuous protection loop", component: Logo9, concept: "Endless Protection" },
    { id: 10, name: "Quantum Particle", description: "Atomic security orbits", component: Logo10, concept: "Quantum Security" }
  ];

  return (
    <div style={{ padding: '3rem', backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <Badge style={{ marginBottom: '1rem', backgroundColor: '#0066CC', color: 'white', padding: '0.5rem 1.5rem', fontSize: '0.875rem' }}>
            Logo Selection
          </Badge>
          <h1 style={{ fontSize: '2.5rem', fontWeight: '800', marginBottom: '1rem', color: '#1F2937' }}>
            Choose Your IrisTechLabs Logo
          </h1>
          <p style={{ fontSize: '1.125rem', color: '#6B7280', maxWidth: '600px', margin: '0 auto' }}>
            Select from 10 unique professional logo designs. Each represents cybersecurity and research innovation.
          </p>
          {selected && (
            <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#DBEAFE', borderRadius: '0.5rem', display: 'inline-block' }}>
              <p style={{ color: '#1E3A8A', fontWeight: '600' }}>
                ✓ Selected: <strong>{logos.find(l => l.id === selected)?.name}</strong>
              </p>
            </div>
          )}
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
          gap: '2rem',
          marginBottom: '3rem'
        }}>
          {logos.map((logo) => {
            const LogoComponent = logo.component;
            const isSelected = selected === logo.id;
            
            return (
              <Card 
                key={logo.id}
                style={{
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: isSelected ? '3px solid #0066CC' : '1px solid #E5E7EB',
                  backgroundColor: isSelected ? '#EFF6FF' : 'white',
                  transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                  boxShadow: isSelected ? '0 10px 25px rgba(0, 102, 204, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}
                onClick={() => setSelected(logo.id)}
              >
                <CardHeader>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    padding: '1.5rem',
                    backgroundColor: '#F3F4F6',
                    borderRadius: '0.75rem',
                    marginBottom: '1rem',
                    position: 'relative'
                  }}>
                    <LogoComponent width="80" height="80" />
                    {isSelected && (
                      <div style={{
                        position: 'absolute',
                        top: '0.5rem',
                        right: '0.5rem',
                        backgroundColor: '#0066CC',
                        borderRadius: '50%',
                        padding: '0.25rem'
                      }}>
                        <CheckCircle2 size={20} color="white" />
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Badge style={{ 
                      backgroundColor: '#E0F2FE', 
                      color: '#0066CC',
                      fontSize: '0.75rem',
                      padding: '0.25rem 0.75rem',
                      marginBottom: '0.5rem'
                    }}>
                      Option {logo.id}
                    </Badge>
                    <CardTitle style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
                      {logo.name}
                    </CardTitle>
                    <CardDescription style={{ fontSize: '0.875rem', marginBottom: '0.75rem' }}>
                      {logo.description}
                    </CardDescription>
                    <div style={{
                      fontSize: '0.8125rem',
                      color: '#0066CC',
                      fontWeight: '600',
                      fontStyle: 'italic'
                    }}>
                      "{logo.concept}"
                    </div>
                  </div>
                </CardHeader>
              </Card>
            );
          })}
        </div>

        {selected && (
          <div style={{ textAlign: 'center', padding: '2rem', backgroundColor: 'white', borderRadius: '1rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '1rem' }}>
              Great Choice! 🎨
            </h3>
            <p style={{ color: '#6B7280', marginBottom: '1.5rem' }}>
              You've selected <strong>{logos.find(l => l.id === selected)?.name}</strong>. 
              This logo will represent IrisTechLabs' identity across the website.
            </p>
            <Button 
              style={{ 
                backgroundColor: '#0066CC', 
                color: 'white', 
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                fontWeight: '600',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer'
              }}
            >
              Apply This Logo
            </Button>
          </div>
        )}

        <div style={{ marginTop: '3rem', textAlign: 'center' }}>
          <p style={{ fontSize: '0.875rem', color: '#9CA3AF' }}>
            💡 <strong>Tip:</strong> Consider which design best represents your brand values: monitoring, intelligence, protection, or innovation.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LogoGallery;
